import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import html2pdf from "html2pdf.js";
import CurrencySymbol, { resolveCurrencyCode, CURRENCY_SYMBOLS } from '../CurrencySymbol';

const ExpressSuccessModal = ({
  show,
  onHide,
  invoiceNo,
  billDetails,
  baseUrl,
  getHeaders,
  settings,
  logoUrl,
  printSettings,
  defaultPrintSettings,
  emailAddress,
  onComplete
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("info");
  const [localEmail, setLocalEmail] = useState(emailAddress || "");

  useEffect(() => {
    setLocalEmail(emailAddress || "");
  }, [emailAddress]);

  const [localSettings, setLocalSettings] = useState(settings || {});
  const [localPrintSettings, setLocalPrintSettings] = useState(printSettings || defaultPrintSettings || {});
  const [localLogoUrl, setLocalLogoUrl] = useState(logoUrl || '');

  useEffect(() => {
    if (show) {
      const fetchSettings = async () => {
        try {
          // Fetch settings same way as Cash.jsx
          const [settingsRes, printRes, logoRes] = await Promise.all([
            axios.get(`${baseUrl || ''}/api/settings`, { headers: getHeaders ? getHeaders() : {} }),
            axios.get(`${baseUrl || ''}/api/print_settings/active`, { headers: getHeaders ? getHeaders() : {} }),
            axios.get(`${baseUrl || ''}/api/logo`, { headers: getHeaders ? getHeaders() : {} })
          ]);
          if (settingsRes.data) setLocalSettings(settingsRes.data);
          if (printRes.data) setLocalPrintSettings(printRes.data);
          if (logoRes.data && logoRes.data.logo) {
            setLocalLogoUrl(`${baseUrl || ''}${logoRes.data.logo}`);
          }
        } catch (err) {
          console.error('Failed to fetch settings in modal', err);
          // Try fallback endpoints
          try {
            const [settingsRes2, printRes2, companyRes] = await Promise.all([
              axios.get(`${baseUrl || ''}/api/settings`, { headers: getHeaders ? getHeaders() : {} }),
              axios.get(`${baseUrl || ''}/api/print-settings`, { headers: getHeaders ? getHeaders() : {} }),
              axios.get(`${baseUrl || ''}/api/company-details`, { headers: getHeaders ? getHeaders() : {} })
            ]);
            if (settingsRes2.data) setLocalSettings(settingsRes2.data);
            if (printRes2.data) setLocalPrintSettings(printRes2.data);
            if (companyRes.data && companyRes.data.logo) {
              setLocalLogoUrl(`${baseUrl || ''}${companyRes.data.logo}`);
            }
          } catch (err2) {
            console.error('Fallback settings fetch also failed', err2);
          }
        }
      };
      fetchSettings();
    }
  }, [show]);

  const isElectron = window && window.process && window.process.type;
  const ipcRenderer = isElectron ? window.require('electron').ipcRenderer : null;
  const navigate = (path) => { if (onComplete) onComplete(); };

  const getCurrencySymbolHtml = () => {
    const activeCompany = localStorage.getItem('active_company');
    const activeBranch = localStorage.getItem('active_branch');
    const code = resolveCurrencyCode(null, activeCompany, activeBranch) || localSettings?.currency;
    if (code === 'AED') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 344.84 299.91" style="height: 11px; width: auto; vertical-align: middle; margin-right: 2px; display: inline-block; fill: #000;"><path d="M342.14,140.96l2.7,2.54v-7.72c0-17-11.92-30.84-26.56-30.84h-23.41C278.49,36.7,222.69,0,139.68,0c-52.86,0-59.65,0-109.71,0,0,0,15.03,12.63,15.03,52.4v52.58h-27.68c-5.38,0-10.43-2.08-14.61-6.01l-2.7-2.54v7.72c0,17.01,11.92,30.84,26.56,30.84h18.44s0,29.99,0,29.99h-27.68c-5.38,0-10.43-2.07-14.61-6.01l-2.7-2.54v7.71c0,17,11.92,30.82,26.56,30.82h18.44s0,54.89,0,54.89c0,38.65-15.03,50.06-15.03,50.06h109.71c85.62,0,139.64-36.96,155.38-104.98h32.46c5.38,0,10.43,2.07,14.61,6l2.7,2.54v-7.71c0-17-11.92-30.83-26.56-30.83h-18.9c.32-4.88.49-9.87.49-15s-.18-10.11-.51-14.99h28.17c5.37,0,10.43,2.07,14.61,6.01ZM89.96,15.01h45.86c61.7,0,97.44,27.33,108.1,89.94l-153.96.02V15.01ZM136.21,284.93h-46.26v-89.98l153.87-.02c-9.97,56.66-42.07,88.38-107.61,90ZM247.34,149.96c0,5.13-.11,10.13-.34,14.99l-157.04.02v-29.99l157.05-.02c.22,4.84.33,9.83.33,15Z"/></svg>`;
    }
    return CURRENCY_SYMBOLS[code] || code;
  };
  const formatCurrencyHtml = (num) => getCurrencySymbolHtml() + ' ' + Number(num).toFixed(2);
  const formatDeliveryAddress = (addr) => addr ? Object.values(addr).filter(Boolean).join(', ') : '';
  const getPrintDeliveryAddressHtml = (addr) => addr ? formatDeliveryAddress(addr) : '';
  const calculateSubtotal = () => billDetails?.subtotal || 0;
  const calculateVAT = () => billDetails?.vat_amount || 0;
  const calculateGrandTotal = () => billDetails?.totalAmount || 0;
  const cashGiven = 0;
  const change = 0;
  const pointsToRedeem = billDetails?.redeemed_points || 0;
  const calculatePointsDiscount = () => billDetails?.redeemed_amount || 0;
  const getVatByRate = () => ({ '18': calculateVAT() });
  const hasDeliveryAddress = !!billDetails?.deliveryAddress;


  const getFormattedDate = (date, dateFormat) => {
    if (!dateFormat) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    const numericFormatter = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: 'numeric' });
    const parts = numericFormatter.formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    switch (dateFormat) {
      case 'dd-mm-yyyy': return `${day.padStart(2, '0')}-${month}-${year}`;
      case 'mm-dd-yyyy': return `${month}-${day.padStart(2, '0')}-${year}`;
      case 'yyyy-mm-dd': return `${year}-${month}-${day.padStart(2, '0')}`;
      default: return `${day.padStart(2, '0')}/${month}/${year}`;
    }
  };

  const getFormattedTime = (date, timeFormat) => {
    if (!timeFormat) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    const hasSeconds = timeFormat.includes(':ss') || timeFormat.includes('ss');
    const is12Hour = timeFormat.includes(' a') || timeFormat.startsWith('hh');
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      ...(hasSeconds && { second: '2-digit' }),
      hour12: is12Hour,
    };
    return date.toLocaleTimeString('en-US', options);
  };

  const generatePrintableContent = (invoiceNoOverride = null, isPreview = false) => {
    if (!billDetails) return "";
    const subtotal = calculateSubtotal();
    const vatAmount = calculateVAT();
    const grandTotal = calculateGrandTotal();
    // Use currentTime for date and time in receipt (real-time like OpeningEntry)
    const formattedDate = getFormattedDate(new Date(), (localSettings?.dateFormat || 'DD/MM/YYYY'));
    const formattedTime = getFormattedTime(new Date(), (localSettings?.timeFormat || 'hh:mm A'));
    const deliveryAddressHtml = getPrintDeliveryAddressHtml(billDetails.deliveryAddress);
    const borderStyle = isPreview ? "border: none;" : "border: 1px solid #000000;";
    const effectivePrintSettings = localPrintSettings;
    const headerHtml = (effectivePrintSettings.headerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const footerHtml = (effectivePrintSettings.footerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const formatter = { format: formatCurrencyHtml }; // Use updated formatter for consistent currency display
    const cashGivenDisplay =
      cashGiven && !isNaN(cashGiven) && Number(cashGiven) > 0
        ? `
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Cash Given:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(cashGiven)}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Change Returned:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: ${change < 0 ? 'red' : 'inherit'};">${formatter.format(change)}</td>
            </tr>
          `
        : "";
    const offerRows = billDetails.items.filter(item => item.originalBasePrice).map(item => {
      return `
        <tr>
          <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${item.item_name}:</td>
          <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;"><span style="text-decoration: line-through;">${formatter.format(item.originalBasePrice * item.quantity)}</span> ${formatter.format(item.basePrice * item.quantity)}</td>
        </tr>
      `;
    }).join('');
    const vatRows = Object.entries(getVatByRate()).map(([rate, amt]) => `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">VAT (${rate}%):</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(amt)}</td>
      </tr>
    `).join('');
    const pointsRow = parseFloat(pointsToRedeem) > 0 ? `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">Points Redeemed (${parseFloat(pointsToRedeem)} pts):</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">-${formatter.format(calculatePointsDiscount())}</td>
      </tr>
    ` : '';
    const earnedPoints = Number(billDetails?.points_earned || 0);
    const earnedPointsRow = earnedPoints > 0 ? `
      <tr>
        <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">Reward Points Earned:</td>
        <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; color: #15803d; font-weight: bold;">+${earnedPoints} pts</td>
      </tr>
    ` : '';
    return `
      <div style="font-family: 'Dubai Regular', Arial, sans-serif; width: 88mm; margin: 0 auto; font-size: 12px; padding: 10px; color: #000000; ${borderStyle} box-sizing: border-box; line-height: 1.2;">
        <div style="display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 15px;">
          ${localLogoUrl ? `<div style="margin-bottom: 5px;"><img src="${localLogoUrl}" alt="Logo" style="max-width: 50px; max-height: 50px; object-fit: contain; border-radius: 3px;"/></div>` : ''}
          <div style="font-family: 'Dubai Regular', Arial, sans-serif; font-size: 12px; width: 100%;">
            ${headerHtml}
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="width: 50%; text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Invoice No</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="width: 50%; text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${invoiceNoOverride || billDetails.invoice_no || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Customer</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.customerName || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Phone</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.phoneNumber || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Email</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.email || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">WhatsApp</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.whatsappNumber || "N/A"}</td>
            </tr>
            ${billDetails.tableNumber && billDetails.tableNumber !== "N/A"
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Table</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.tableNumber}</td>
                  </tr>
                `
        : ""
      }
            ${hasDeliveryAddress && deliveryAddressHtml
        ? `
                  <tr>
                    <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; vertical-align: top;">Delivery Address</td>
                    <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                    <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: pre-line; word-break: break-all;">${deliveryAddressHtml}</td>
                  </tr>
                `
        : ""
      }
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Payment Mode</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${billDetails.payments?.[0]?.mode_of_payment || "CASH"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Date</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedDate}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Time</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedTime}</td>
            </tr>
          </tbody>
        </table>
        <table style="width: 100%; margin-bottom: 10px; border-collapse: collapse; border: 1px solid #000000; table-layout: fixed;">
          <thead>
            <tr style="border-bottom: 1px dashed #000000;">
              <th style="text-align: left; width: 40%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Item</th>
              <th style="text-align: center; width: 15%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Qty</th>
              <th style="text-align: right; width: 20%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Price</th>
              <th style="text-align: right; width: 25%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${billDetails.items
        .map((item) => {
          const { basePrice, icePrice, spicyPrice, addonTotal, comboTotal } = calculateItemPrices(item);
          return `
                  <tr>
                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px; vertical-align: top;">${getItemDisplayName(item)}</td>
                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${item.quantity}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice)}</td>
                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatter.format(basePrice * item.quantity)}</td>
                  </tr>
                  ${item.comboItems && item.comboItems.length > 0
              ? item.comboItems
                .map(
                  (comboItem) => `
                              <tr>
                                <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${comboItem.name}</td>
                                <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                                <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;"></td>
                              </tr>
                            `
                )
                .join("")
              : ""
            }
                  ${item.icePreference === "with_ice" && icePrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Ice</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(icePrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.isSpicy && spicyPrice > 0
              ? `
                        <tr>
                          <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ Spicy</td>
                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice)}</td>
                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(spicyPrice * item.quantity)}</td>
                        </tr>
                      `
              : ""
            }
                  ${item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0
              ? Object.entries(item.customVariantsDetails)
                .map(
                  ([variantName, variant]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${variant.heading}: ${variant.name}</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.customVariantsQuantities?.[variantName] || 1}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(variant.price * (item.customVariantsQuantities?.[variantName] || 1))}</td>
                            </tr>
                          `
                )
                .join("")
                .join("")
              : ""
            }
                  ${item.itemToppings && Object.keys(item.itemToppings).length > 0
              ? Object.entries(item.itemToppings)
                .map(
                  ([name, top]) => `
                            <tr>
                              <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                              <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${item.quantity * (top.qty || 1)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                              <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * item.quantity * (top.qty || 1))}</td>
                            </tr>
                          `
                )
                .join("")
              : ""
            }
                  ${(item.addons && item.addons.length > 0)
              ? item.addons
                .map(
                  (addon) => {
                    const aQty = Number(addon.addon_quantity) || 1;
                    const aPrice = Number(addon.addon_price) || Number(addon.price) || 0;
                    const aName = addon.addon_name || addon.name || addon.name1 || '';
                    const aSpicyPrice = Number(addon.addon_spicy_price) || Number(addon.spicyPrice) || Number(addon.spicy_price) || 0;
                    return `
                                  <tr>
                                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold; vertical-align: top;">+ ${aName}${addon.size ? ` (${addon.size})` : ''}</td>
                                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${aQty}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(aPrice)}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(aPrice * aQty)}</td>
                                  </tr>
                                  ${addon.isSpicy && aSpicyPrice > 0
                        ? `<tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #666; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${aQty}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(aSpicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(aSpicyPrice * aQty)}</td>
                                        </tr>`
                        : ''
                      }
                                  ${addon.custom_variants && Object.keys(addon.custom_variants).length > 0
                        ? Object.entries(addon.custom_variants).map(([cvName, cv]) =>
                          `<tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${cv.heading || cvName}: ${cv.name}</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${aQty}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price * aQty)}</td>
                                          </tr>`
                        ).join('')
                        : ''
                      }
                                  ${addon.addon_toppings && Object.keys(addon.addon_toppings).length > 0
                        ? Object.entries(addon.addon_toppings).map(([topName, top]) =>
                          `<tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${aQty * (top.qty || 1)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * aQty * (top.qty || 1))}</td>
                                          </tr>`
                        ).join('')
                        : ''
                      }
                                `;
                  }
                )
                .join('')
              : ''
            }
                  ${((item.selectedCombos && item.selectedCombos.length > 0) || (item.combos && item.combos.length > 0))
              ? (item.selectedCombos || item.combos)
                .map(
                  (combo) => {
                    const cQty = Number(combo.combo_quantity) || 1;
                    const cPrice = Number(combo.combo_price) || Number(combo.price) || 0;
                    const cName = combo.name1 || combo.name || combo.combo_name || '';
                    const cSpicyPrice = Number(combo.combo_spicy_price) || Number(combo.spicyPrice) || Number(combo.spicy_price) || 0;
                    return `
                                  <tr>
                                    <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold; vertical-align: top;">+ ${cName}${combo.size ? ` (${combo.size})` : ''}</td>
                                    <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${cQty}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(cPrice)}</td>
                                    <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(cPrice * cQty)}</td>
                                  </tr>
                                  ${combo.isSpicy && cSpicyPrice > 0
                        ? `<tr>
                                          <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px; color: #666; vertical-align: top;">+ Spicy</td>
                                          <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${cQty}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(cSpicyPrice)}</td>
                                          <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 10px;">${formatter.format(cSpicyPrice * cQty)}</td>
                                        </tr>`
                        : ''
                      }
                                  ${combo.custom_variants && Object.keys(combo.custom_variants).length > 0
                        ? Object.entries(combo.custom_variants).map(([cvName, cv]) =>
                          `<tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${cv.heading || cvName}: ${cv.name}</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${cQty}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(cv.price * cQty)}</td>
                                          </tr>`
                        ).join('')
                        : ''
                      }
                                  ${combo.combo_toppings && Object.keys(combo.combo_toppings).length > 0
                        ? Object.entries(combo.combo_toppings).map(([topName, top]) =>
                          `<tr>
                                            <td style="text-align: left; padding: 2px 8px 2px 24px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${top.name} (Topping)</td>
                                            <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${cQty * (top.qty || 1)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price)}</td>
                                            <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatter.format(top.price * cQty * (top.qty || 1))}</td>
                                          </tr>`
                        ).join('')
                        : ''
                      }
                                `;
                  }
                )
                .join('')
              : ''
            }
                `;
        })
        .join("")}
          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total Quantity:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${billDetails.items.reduce((sum, item) => sum + item.quantity, 0)}</td>
            </tr>
            ${offerRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">Subtotal:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatter.format(subtotal)}</td>
            </tr>
            ${vatRows}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total VAT:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatter.format(vatAmount)}</td>
            </tr>
            ${pointsRow}
            ${earnedPointsRow}
            ${cashGivenDisplay}
            <tr style="font-weight: bold; border-top: 2px solid #000;">
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">Grand Total:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">${formatter.format(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top: 15px; width: 100%;">
          ${footerHtml}
        </div>
      </div>
    `;
  }

  const handlePrint = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const content = generatePrintableContent(billDetails?.invoice_no);

      if (isElectron && ipcRenderer) {
        ipcRenderer.send("open-print-preview", content);
        ipcRenderer.once("print-preview-response", (event, response) => {
          setIsLoading(false);
          if (response.success) {
            if (onHide) onHide();
            else navigate("/frontpage");
          } else {
            setError("Failed to open print preview: " + response.error);
            setWarningMessage("Print preview failed: " + response.error);
            setWarningType("warning");
          }
        });
      } else {
        const win = window.open("", "_blank");
        win.document.write(`
          <html>
            <head>
              <title>Receipt - Invoice ${billDetails?.invoice_no || "N/A"}</title>
              <style>
                @media print {
                  body { margin: 0; }
                  @page { margin: 0; size: 88mm auto; }
                }
                body { margin: 0; }
              </style>
            </head>
            <body>
              ${content}
            </body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
        setIsLoading(false);

        if (onHide) onHide();
        else navigate("/frontpage");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Printing failed: " + err.message);
      setWarningMessage("Printing failed: " + err.message);
      setWarningType("warning");
    }
  }

  const handleEmail = async () => {
    if (!localEmail || !localEmail.includes("@")) {
      setWarningMessage("Please enter a valid email address!");
      setWarningType("warning");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let htmlContent = generatePrintableContent(billDetails?.invoice_no);
      const originalHtmlContent = htmlContent;
      const publicUrl = "http://157.173.96.252:6034";
      if (htmlContent) {
        htmlContent = htmlContent.replaceAll('src="/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/static/uploads/', `src="${publicUrl}/static/uploads/`);
        htmlContent = htmlContent.replaceAll('src="/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:6034/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/api/images/', `src="${publicUrl}/api/images/`);
        htmlContent = htmlContent.replaceAll('src="http://localhost:3000/assets/', 'src="http://157.173.96.252:3000/assets/');
      }
      let emailWrapperHtml = "";
      if (htmlContent) {
        htmlContent = htmlContent.replace(/<img[^>]*Dirham%20Currency%20Symbol%20-%20Black\.svg[^>]*>/gi, '<span style="font-size: 11px; font-weight: bold; margin-right: 2px;">AED</span>');
        const customerName = billDetails?.customerName && billDetails.customerName !== 'N/A' ? billDetails.customerName : 'Customer';
        const activeBranch = localStorage.getItem('active_branch');
        const branchName = (activeBranch && activeBranch !== 'All Branches') ? activeBranch : null;
        const restName = localSettings?.restaurantName || branchName || localStorage.getItem('active_company') || 'Our Restaurant';
        const finalLogoUrl = localLogoUrl ? localLogoUrl.replaceAll('http://localhost:6034', publicUrl).replaceAll('http://localhost:3000', publicUrl).replaceAll('src="/static', `src="${publicUrl}/static`) : '';

        let emailSettingsEmail = localSettings?.email || 'Email not provided';
        try {
          const esRes = await axios.get('/api/get-email-settings', {
            headers: {
              'X-Company-Name': localStorage.getItem('active_company') || '',
              'X-Branch-Name': localStorage.getItem('active_branch') || ''
            }
          });
          if (esRes.data && esRes.data.settings) {
            emailSettingsEmail = esRes.data.settings.admin_email || esRes.data.settings.sender_email || emailSettingsEmail;
          }
        } catch (esErr) {
          console.error("Failed to fetch email settings", esErr);
        }
        const effectivePrintSettings = localPrintSettings;
        const phoneBlock = (effectivePrintSettings.headerBlocks || []).find(b =>
          (b.label && b.label.toLowerCase().includes('phone')) ||
          (b.text && (b.text.includes('+') || /\d{9,}/.test(b.text.replace(/\s|-/g, ''))))
        );
        const displayPhone = (phoneBlock && phoneBlock.text) ? phoneBlock.text : (localSettings?.phone || 'Phone not provided');

        emailWrapperHtml = `
<div style="background-color: #f4f6f8; padding: 40px 20px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 15px rgba(0,0,0,0.05);">
    
    <!-- 1. Restaurant Logo Section -->
    <div style="text-align: center; padding: 20px;">
      ${finalLogoUrl ? `<img src="${finalLogoUrl}" alt="Restaurant Logo" style="max-width: 120px; max-height: 80px;" />` : ''}
    </div>

    <!-- 2. Header Section (Purple Background) -->
    <div style="background-color: #604BE8; color: #ffffff; padding: 25px 20px; text-align: center;">
      <h1 style="margin: 0; font-size: 24px; font-weight: 600;">🍽️ Thank You for Your Order!</h1>
    </div>

    <!-- 3. Personalized Greeting Section -->
    <div style="padding: 30px 30px 15px 30px; font-size: 16px; color: #333333; line-height: 1.6;">
      <p style="margin-top: 0;">Hi ${customerName},</p>
      
      <p><strong>Your order has been confirmed successfully.</strong></p>
      
      <p>Thank you for choosing <strong>${restName}</strong>. Your receipt is provided below for your reference.</p>
      
      <p>We hope you enjoyed your meal and had a great experience with us. If you have any questions about your order or need any assistance, we're always happy to help.</p>
      
      <p>We look forward to serving you again soon.</p>
    </div>

    <!-- 4. The Invoice (Receipt) -->
    <div style="padding: 15px 30px 30px 30px; display: flex; justify-content: center;">
       ${htmlContent}
    </div>

    <!-- 5. Footer Section -->
    <div style="background-color: #f1f5f9; padding: 25px; text-align: left; border-top: 1px solid #e2e8f0; font-size: 15px; color: #475569;">
      <h3 style="margin-top: 0; color: #334155; font-size: 18px;">Need Assistance?</h3>
      <p style="margin: 5px 0;">📞 ${displayPhone}</p>
      <p style="margin: 5px 0;">📧 ${emailSettingsEmail}</p>
      <p style="margin: 5px 0;">🌐 <a href="${window.location.origin}" style="color: #604BE8; text-decoration: none;">${window.location.origin}</a></p>
      
      <p style="margin-top: 20px; font-weight: bold; color: #334155; text-align: center;">Thank you for being our valued customer.<br/>We look forward to serving you again soon! ❤️</p>
    </div>

  </div>
</div>`;
      }

      let pdfBase64 = null;
      try {
        const opt = {
          margin: 10,
          filename: `Receipt_${billDetails?.invoice_no || "N/A"}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, allowTaint: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalHtmlContent || htmlContent;
        const style = document.createElement('style');
        style.innerHTML = `
          body { font-family: 'Dubai Regular', Arial, sans-serif; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 4px; }
        `;
        tempDiv.appendChild(style);
        document.body.appendChild(tempDiv);

        pdfBase64 = await html2pdf().set(opt).from(tempDiv).outputPdf('datauristring');
        document.body.removeChild(tempDiv);
      } catch (pdfError) {
        console.error("Error generating PDF attachment:", pdfError);
      }

      const emailContent = {
        to: localEmail,
        subject: `Receipt from ${localSettings?.restaurantName || 'My Restaurant'} - ${billDetails?.invoice_no || "N/A"}`,
        html: emailWrapperHtml,
        pdf_base64: pdfBase64,
        invoice_no: billDetails?.invoice_no || "N/A"
      };

      const response = await axios.post("/api/send-email", emailContent, {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      });
      setIsLoading(false);
      if (response.data.success) {
        setWarningMessage("Receipt sent successfully to " + localEmail);
        setWarningType("success");
      } else {
        setError("Failed to send email: " + response.data.message);
        setWarningMessage("Failed to send email: " + response.data.message);
        setWarningType("warning");
      }
    } catch (err) {
      setIsLoading(false);
      setError("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningMessage("Email sending failed: " + (err.response?.data?.message || err.message));
      setWarningType("warning");
    }
  }

  const calculateItemPrices = (item) => {
    if (item.isCombo) {
      return {
        basePrice: Number(item.basePrice) || 0,
        icePrice: 0,
        spicyPrice: 0,
        addonTotal: 0,
        comboTotal: 0,
        customVariantsTotal: 0,
        itemToppingsTotal: 0,
        totalAmount: (Number(item.basePrice) || 0) * item.quantity,
      };
    }
    const basePrice = Number(item.basePrice) || 0;
    const icePrice = item.icePreference === "with_ice" ? Number(item.icePrice) || 0 : 0;
    const spicyPrice = item.isSpicy ? Number(item.spicyPrice) || Number(item.spicy_price) || 0 : 0;
    const addonTotal =
      item.addons && item.addons.length > 0
        ? item.addons.reduce(
          (sum, addon) => sum + Number(addon.addon_total_price) * addon.addon_quantity,
          0
        )
        : 0;
    const comboTotal =
      item.combos && item.combos.length > 0
        ? item.combos.reduce(
          (sum, combo) => sum + Number(combo.combo_total_price) * combo.combo_quantity,
          0
        )
        : 0;
    const customVariantsTotal = item.customVariantsDetails
      ? Object.values(item.customVariantsDetails).reduce(
        (sum, variant) => sum + (Number(variant.price) || 0) * (item.customVariantsQuantities?.[variant.name] || 1),
        0
      ) * item.quantity
      : 0;
    const itemToppingsTotal = item.itemToppings
      ? Object.values(item.itemToppings).reduce(
        (sum, t) => sum + (Number(t.price) || 0) * (Number(t.qty) || 1),
        0
      )
      : 0;
    const totalAmount = (basePrice + icePrice + spicyPrice + customVariantsTotal + itemToppingsTotal) * item.quantity + addonTotal + comboTotal;
    return { basePrice, icePrice, spicyPrice, addonTotal, comboTotal, customVariantsTotal, itemToppingsTotal, totalAmount };
  }

  const getItemDisplayName = (item) => {
    const sizeDisplay = item.selectedSize ? ` (${item.selectedSize})` : "";
    return `${item.item_name}${sizeDisplay}`;
  }



  return (
    <Modal show={show} onHide={onHide} centered size="md" className="glass-modal">
      <Modal.Body className="text-center p-4" style={{ background: '#ffffff', borderRadius: '16px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#dcfce3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px auto' }}>
            <span style={{ fontSize: '30px', color: '#10b981' }}>✓</span>
          </div>
          <h4 style={{ color: '#1e293b', fontWeight: '700', marginBottom: '5px' }}>Payment Successful!</h4>
          <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '15px' }}>Invoice has been generated for #{invoiceNo || billDetails?.invoice_no || 'N/A'}</p>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '15px', borderBottom: '1px dashed #e2e8f0', paddingBottom: '15px' }}>
            {localLogoUrl && <div style={{ marginBottom: '5px' }}><img src={localLogoUrl} alt="Logo" style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain', borderRadius: '3px' }} /></div>}
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', width: '100%', color: '#334155' }} dangerouslySetInnerHTML={{ __html: (localPrintSettings?.headerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('') }} />
          </div>
        </div>

        {warningMessage && <p style={{ color: warningType === 'error' ? '#ef4444' : '#f59e0b', fontSize: '14px' }}>{warningMessage}</p>}
        {error && <p style={{ color: '#ef4444', fontSize: '14px' }}>{error}</p>}

        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'left', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '15px' }}>
            <div style={{ flex: '1 1 45%' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>CUSTOMER</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>{billDetails?.customerName || 'Walk-in'}</p>
            </div>
            <div style={{ flex: '1 1 45%' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>PHONE</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>{billDetails?.phoneNumber || 'N/A'}</p>
            </div>
            <div style={{ flex: '1 1 45%' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>EMAIL</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>{billDetails?.email || 'N/A'}</p>
            </div>
            <div style={{ flex: '1 1 45%' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>INVOICE NO.</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>#{invoiceNo || billDetails?.invoice_no || 'N/A'}</p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #cbd5e1', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>
            <span style={{ flex: 2 }}>ITEM</span>
            <span style={{ flex: 1, textAlign: 'center' }}>QTY</span>
            <span style={{ flex: 1, textAlign: 'right' }}>PRICE</span>
          </div>

          <div style={{ marginBottom: '15px' }}>
            {(billDetails?.items || []).map((item, idx) => (
              <React.Fragment key={idx}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #e2e8f0', fontSize: '13px', color: '#000', fontWeight: '600' }}>
                  <span style={{ flex: 2 }}>{getItemDisplayName(item) || item.item_name || item.name}</span>
                  <span style={{ flex: 1, textAlign: 'center', color: '#604BE8' }}>{item.quantity || 1}</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                      <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                      <span style={{ flex: 1, textAlign: 'right' }}>{Number((item.basePrice || 0) * (item.quantity || 1)).toFixed(2)}</span>
                    </span>
                  </span>
                </div>

                {item.isSpicy && (Number(item.spicyPrice) > 0 || Number(item.spicy_price) > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                    <span style={{ flex: 2, paddingLeft: '12px' }}>+ Spicy</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity || 1}</span>
                    <span style={{ flex: 1, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                        <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                        <span style={{ flex: 1, textAlign: 'right' }}>{Number((item.spicyPrice || item.spicy_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                      </span>
                    </span>
                  </div>
                )}
                {item.icePreference === "with_ice" && (Number(item.icePrice) > 0 || Number(item.ice_price) > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                    <span style={{ flex: 2, paddingLeft: '12px' }}>+ With Ice</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>{item.quantity || 1}</span>
                    <span style={{ flex: 1, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                        <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                        <span style={{ flex: 1, textAlign: 'right' }}>{Number((item.icePrice || item.ice_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                      </span>
                    </span>
                  </div>
                )}
                {item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0 && Object.entries(item.customVariantsDetails).map(([variantName, variant], idx) => {
                  const vQty = (item.customVariantsQuantities?.[variantName] || 1) * (item.quantity || 1);
                  return (
                    <div key={`cv-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                      <span style={{ flex: 2, paddingLeft: '12px' }}>+ {variant.heading || variantName}: {variant.name}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{vQty}</span>
                      <span style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                          <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                          <span style={{ flex: 1, textAlign: 'right' }}>{Number((variant.price || 0) * vQty).toFixed(2)}</span>
                        </span>
                      </span>
                    </div>
                  );
                })}
                {item.itemToppings && Object.keys(item.itemToppings).length > 0 && Object.entries(item.itemToppings).map(([toppingName, topping], idx) => {
                  const tQty = (Number(topping.qty) || 1);
                  return (
                    <div key={`topping-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                      <span style={{ flex: 2, paddingLeft: '12px' }}>+ {toppingName}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{tQty}</span>
                      <span style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                          <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                          <span style={{ flex: 1, textAlign: 'right' }}>{Number((topping.price || 0) * tQty).toFixed(2)}</span>
                        </span>
                      </span>
                    </div>
                  );
                })}
                {item.addons && (Array.isArray(item.addons) ? item.addons : []).map((addon, aIdx) => {
                  const aQty = Number(addon.addon_quantity) || 1;
                  return (
                    <div key={`addon-${aIdx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                      <span style={{ flex: 2, paddingLeft: '12px' }}>+ {addon.addon_name || addon.name1}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{aQty}</span>
                      <span style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                          <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                          <span style={{ flex: 1, textAlign: 'right' }}>{Number((addon.addon_price || 0) * aQty).toFixed(2)}</span>
                        </span>
                      </span>
                    </div>
                  );
                })}

                {item.selectedCombos && (Array.isArray(item.selectedCombos) ? item.selectedCombos : []).map((combo, cIdx) => {
                  const cQty = Number(combo.combo_quantity) || 1;
                  return (
                    <div key={`combo-${cIdx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#64748b' }}>
                      <span style={{ flex: 2, paddingLeft: '12px' }}>+ {combo.name1}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{cQty}</span>
                      <span style={{ flex: 1, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                          <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                          <span style={{ flex: 1, textAlign: 'right' }}>{Number((combo.combo_price || 0) * cQty).toFixed(2)}</span>
                        </span>
                      </span>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#475569' }}>
              <span>Subtotal</span>
              <span style={{ fontWeight: '600', color: '#1e293b' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                  <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                  <span style={{ flex: 1, textAlign: 'right' }}>{Number(calculateSubtotal() || 0).toFixed(2)}</span>
                </span>
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
              <span>Total VAT</span>
              <span style={{ fontWeight: '600', color: '#1e293b' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                  <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                  <span style={{ flex: 1, textAlign: 'right' }}>{Number(calculateVAT() || 0).toFixed(2)}</span>
                </span>
              </span>
            </div>
            {parseFloat(pointsToRedeem) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#10b981', fontWeight: '600' }}>
                <span>Points Redeemed ({pointsToRedeem})</span>
                <span>
                  -<span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                    <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                    <span style={{ flex: 1, textAlign: 'right' }}>{Number(calculatePointsDiscount() || 0).toFixed(2)}</span>
                  </span>
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Grand Total</span>
              <span style={{ fontSize: '20px', fontWeight: '800', color: '#10b981' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', width: '120px' }}>
                  <span style={{ width: '30px', display: 'flex', justifyContent: 'flex-start' }}><CurrencySymbol size={13} /></span>
                  <span style={{ flex: 1, textAlign: 'right' }}>{Number(calculateGrandTotal() || 0).toFixed(2)}</span>
                </span>
              </span>
            </div>
            {Number(billDetails?.points_earned || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #cbd5e1', fontSize: '14px', color: '#16a34a', fontWeight: '700' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🎁 Reward Points Earned</span>
                </span>
                <span style={{ background: '#dcfce7', padding: '3px 10px', borderRadius: '12px' }}>
                  +{Number(billDetails.points_earned)} pts
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ padding: '0 5px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block', textAlign: 'left' }}>
              Email Address
            </label>
            <input
              type="email"
              value={localEmail}
              onChange={(e) => setLocalEmail(e.target.value)}
              placeholder="customer@example.com"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                transition: 'border-color 0.2s'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              onClick={handlePrint}
              disabled={isLoading}
              style={{ flex: 1, padding: '12px', fontWeight: '600', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', background: '#f1f5f9', color: '#334155', border: 'none' }}
            >
              {isLoading ? <Spinner size="sm" /> : '🖨️'} Print Bill
            </Button>
            <Button
              onClick={handleEmail}
              disabled={isLoading || !localEmail}
              style={{ flex: 1, padding: '12px', fontWeight: '600', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', background: '#604BE8', color: '#fff', border: 'none' }}
            >
              {isLoading ? <Spinner size="sm" /> : '✉️'} Send Email
            </Button>
          </div>
          <Button
            onClick={onHide}
            variant="outline-secondary"
            style={{ marginTop: '10px' }}
          >
            Done
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ExpressSuccessModal;
